import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router';

import { resolveShipping } from '@/features/storefront/api/storefront';
import { splitDiscount } from '@/features/storefront/bundleDiscount';
import {
  MIN_LINE_QUANTITY,
  cartOverStock,
  clampLineQuantity,
  freeShippingProgress,
  lineMaxQuantity,
  shopCopy,
} from '@/features/storefront/cartLimits';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { focalStyle } from '@/features/storefront/focal';
import { useCart, useCartMutations } from '@/features/storefront/hooks/useCart';
import { resolveVariant } from '@/features/storefront/layouts/families';
import { useThemeLayout } from '@/features/storefront/layouts/useThemeLayout';
import { pluginEnabled, themePlugin } from '@/features/storefront/plugins/registry';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import { useStorefrontTheme } from '@/features/storefront/useStorefrontTheme';
import { fixedImageSizes } from '@/lib/imageRenditions';
import { formatMoney } from '@/lib/money';

/**
 * The quick cart: the basket as a drawer that slides in from the right.
 *
 * It is a plugin (`plugins/registry.ts`): the merchant switches it on for
 * any theme, and a theme designed around it (`ThemePackage.quickCart`) has it
 * on until they say otherwise. While it is on, two things open it instead of
 * going to the cart page - the header's cart link and, unless the merchant
 * turned that off, "Add to cart" on a product page - so a shopper adds kit and
 * carries on browsing. The
 * cart page is still there, one button away, and is still where a shopper
 * lands with JavaScript off, a middle-click or a modifier key.
 *
 * It reads and writes the same cart as the cart page (`useCart`,
 * `useCartMutations`), so it works identically in the live shop and in a
 * theme's preview shop, whose data source keeps its own in-memory cart.
 *
 * The open state lives in {@link QuickCartProvider}, mounted by both shells
 * around the header and the page, because the two things that open it - the
 * header and the product page - are nowhere near each other in the tree.
 */

interface QuickCartState {
  open: boolean;
  openCart: () => void;
  closeCart: () => void;
}

const QuickCartContext = createContext<QuickCartState | null>(null);

/**
 * How many suggestions the drawer offers under the lines. More than fit
 * across the panel on purpose: the row scrolls, and the arrows beside its
 * heading step through the rest.
 */
const SUGGESTIONS = 8;

/** Holds whether the drawer is open, and draws it when the plugin is on. */
export function QuickCartProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const openCart = useCallback(() => setOpen(true), []);
  const closeCart = useCallback(() => setOpen(false), []);
  const value = useMemo(() => ({ open, openCart, closeCart }), [open, openCart, closeCart]);

  return (
    <QuickCartContext.Provider value={value}>
      {children}
      {open ? <QuickCartDrawer onClose={closeCart} /> : null}
    </QuickCartContext.Provider>
  );
}

/**
 * Whether the quick cart is on for this shop, and the way to open it.
 *
 * On only where the plugin is (the merchant's choice, or the theme's) *and* a
 * shell has mounted the provider - so a component rendered outside either
 * shell (a test, the customiser) keeps behaving exactly as it did before the
 * drawer existed. `openOnAdd` is whether "Add to cart" should open it too.
 */
export function useQuickCart(): { enabled: boolean; openOnAdd: boolean; openCart: () => void } {
  const state = useContext(QuickCartContext);
  const theme = useStorefrontTheme();
  const family = resolveVariant(useThemeLayout(), 'header');
  const enabled = state !== null && pluginEnabled(theme, 'quick_cart', family);
  const openOnAdd = enabled && themePlugin(theme, 'quick_cart').open_on_add;
  return { enabled, openOnAdd, openCart: state?.openCart ?? noop };
}

function noop() {}

function QuickCartDrawer({ onClose }: { onClose: () => void }) {
  const text = useShopContent();
  const paths = useStorefrontPaths();
  const features = useStorefrontFeatures();
  const settings = themePlugin(useStorefrontTheme(), 'quick_cart');
  const location = useLocation();
  const cart = useCart();
  const { setItem } = useCartMutations();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const context = useQuery({ queryKey: [...scope, 'context'], queryFn: () => source.context() });
  const closeRef = useRef<HTMLButtonElement>(null);
  const [shown, setShown] = useState(false);

  const items = cart.data?.items ?? [];
  const count = items.reduce((sum, item) => sum + item.quantity, 0);
  const progress = settings.free_shipping_bar
    ? freeShippingProgress(cart.data, resolveShipping(context.data).free_shipping_threshold)
    : null;
  // The bundle's share of the discount is its own line; the code line the rest.
  const split = splitDiscount(cart.data);

  // Suggestions for the first line, leaving out what is already in the cart.
  const firstSlug = settings.recommendations ? (items[0]?.product_slug ?? null) : null;
  const related = useQuery({
    queryKey: [...scope, 'product', firstSlug, 'related'],
    queryFn: () => source.relatedProducts(firstSlug as string, 12),
    enabled: firstSlug !== null,
  });
  const inCart = new Set(items.map((item) => item.product_slug));
  const suggestions = [...(related.data?.similar ?? []), ...(related.data?.also_liked ?? [])]
    .filter((product, index, all) => !inCart.has(product.slug) && all.findIndex((p) => p.slug === product.slug) === index)
    .slice(0, SUGGESTIONS);

  // The suggestion row: which ends it is at, so an arrow with nowhere to go
  // is disabled, and a step of one card - its width plus the gap - per click.
  const rowId = useId();
  const rowRef = useRef<HTMLDivElement>(null);
  const [rowEnds, setRowEnds] = useState({ start: true, end: true });
  const measureRow = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    const start = row.scrollLeft <= 1;
    const end = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1;
    setRowEnds((was) => (was.start === start && was.end === end ? was : { start, end }));
  }, []);
  useEffect(() => {
    measureRow();
    window.addEventListener('resize', measureRow);
    return () => window.removeEventListener('resize', measureRow);
  }, [measureRow, suggestions.length]);
  const stepRow = (direction: -1 | 1) => {
    const row = rowRef.current;
    const card = row?.firstElementChild as HTMLElement | null;
    if (!row || !card) return;
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    row.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' });
  };

  // Closes when the shopper goes somewhere else - a product, the cart page,
  // checkout - which is every link inside the drawer.
  const openedAt = useRef(`${location.pathname}${location.search}`);
  useEffect(() => {
    if (`${location.pathname}${location.search}` !== openedAt.current) onClose();
  }, [location.pathname, location.search, onClose]);

  // Slide in on the frame after mounting, lock the page behind, focus the
  // close button, close on Escape, and hand focus back to whatever opened it.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const frame = window.requestAnimationFrame(() => setShown(true));
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, [onClose]);

  // Capped at what is in stock as well as at the stepper's bounds: a line
  // with one unit left cannot be stepped to five and found out at the till.
  const setQuantity = (item: (typeof items)[number], next: number) => {
    const quantity = clampLineQuantity(item, next);
    if (quantity === item.quantity) return;
    setItem.mutate({ variantId: item.variant_id, quantity });
  };
  // A line over stock holds the till until it is brought down.
  const overStock = cartOverStock(items);

  const hairline = { borderColor: 'var(--storefront-surface)' };

  // Rendered where the provider stands - inside the shell's themed element -
  // rather than portalled to <body>, so it wears the shop's colour variables.
  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-quick-cart>
      <button
        type="button"
        tabIndex={-1}
        aria-label={text('cart.drawer_close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/60 transition-opacity duration-300"
        style={{ opacity: shown ? 1 : 0 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={text('cart.title')}
        className="relative flex h-full w-full max-w-[28rem] flex-col shadow-2xl transition-transform duration-300 ease-out"
        style={{
          backgroundColor: 'var(--storefront-background)',
          color: 'var(--storefront-text)',
          transform: shown ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        <div className="flex items-center justify-between border-b px-6 py-5" style={hairline}>
          <h2 className="text-2xl" style={STOREFRONT_HEADING_STYLE}>
            {text('cart.drawer_title', { count })}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={text('cart.drawer_close')}
            className="-me-1 p-1 hover:opacity-70"
          >
            <X className="size-6" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6">
          {progress && items.length > 0 ? (
            <div className="border-b py-5 text-center text-sm" style={hairline}>
              <div
                className="mb-3 h-1.5 w-full overflow-hidden rounded-full"
                style={{ backgroundColor: 'var(--storefront-surface)' }}
              >
                <div
                  className="h-full rounded-full transition-[width] duration-500"
                  style={{
                    width: `${Math.round(progress.ratio * 100)}%`,
                    backgroundColor: 'var(--storefront-primary)',
                  }}
                />
              </div>
              {progress.remaining > 0
                ? text('cart.free_shipping_remaining', {
                    amount: formatMoney(progress.remaining.toFixed(2)),
                  })
                : text('cart.free_shipping_unlocked')}
            </div>
          ) : null}

          {cart.isPending ? null : items.length === 0 ? (
            <div className="py-16 text-center">
              <p style={{ color: 'var(--storefront-muted)' }}>{text('cart.empty')}</p>
              <Link
                to={paths.products()}
                className="mt-6 inline-block px-6 py-3 text-sm font-semibold uppercase tracking-wide"
                style={{ ...STOREFRONT_BUTTON_STYLE, borderRadius: 'var(--storefront-radius)' }}
              >
                {text('cart.continue')}
              </Link>
            </div>
          ) : (
            <ul>
              {items.map((item) => (
                <li
                  key={item.variant_id}
                  className="flex gap-4 border-b py-5"
                  style={hairline}
                >
                  <Link
                    to={paths.product(item.product_slug)}
                    className="shrink-0"
                    aria-label={item.product_name}
                    tabIndex={-1}
                  >
                    {item.image_url ? (
                      <span
                        className="block size-20 overflow-hidden"
                        style={{ borderRadius: 'var(--storefront-radius)' }}
                      >
                        <StorefrontImage
                          url={item.image_url}
                          sizes={fixedImageSizes(80)}
                          alt=""
                          className="size-full object-cover"
                          style={focalStyle(item.image_focal)}
                        />
                      </span>
                    ) : (
                      <span
                        className="block size-20"
                        style={{
                          backgroundColor: 'var(--storefront-surface)',
                          borderRadius: 'var(--storefront-radius)',
                        }}
                      />
                    )}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={paths.product(item.product_slug)}
                      className="font-semibold hover:underline"
                    >
                      {item.product_name}
                    </Link>
                    {Object.values(item.options).length > 0 ? (
                      <p className="mt-0.5 text-sm" style={{ color: 'var(--storefront-muted)' }}>
                        {Object.values(item.options).join(' / ')}
                      </p>
                    ) : null}
                    <p className="mt-1 text-sm">{formatMoney(item.line_subtotal)}</p>
                    {item.available !== null && item.available < item.quantity ? (
                      <p className="mt-1 text-xs text-red-500">
                        {text('cart.low_stock', { available: item.available })}
                      </p>
                    ) : null}
                    <div className="mt-3 flex items-center gap-4">
                      <div className="inline-flex items-center border" style={hairline}>
                        <button
                          type="button"
                          aria-label={text('product_page.quantity_decrease')}
                          disabled={item.quantity <= MIN_LINE_QUANTITY}
                          onClick={() => setQuantity(item, item.quantity - 1)}
                          className="h-9 w-9 text-base disabled:opacity-40"
                        >
                          &minus;
                        </button>
                        <span
                          className="w-9 text-center text-sm"
                          aria-label={text('cart.quantity', { product: item.product_name })}
                        >
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          aria-label={text('product_page.quantity_increase')}
                          disabled={item.quantity >= lineMaxQuantity(item)}
                          onClick={() => setQuantity(item, item.quantity + 1)}
                          className="h-9 w-9 text-base disabled:opacity-40"
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => setItem.mutate({ variantId: item.variant_id, quantity: 0 })}
                        className="text-sm underline hover:opacity-70"
                      >
                        {text('cart.remove')}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {suggestions.length > 0 ? (
            <div className="py-6">
              <div className="mb-4 flex items-center justify-between gap-4">
                <h3 className="text-lg" style={STOREFRONT_HEADING_STYLE}>
                  {text('cart.drawer_recommended')}
                </h3>
                {/* Hidden while every suggestion already fits across the panel. */}
                {rowEnds.start && rowEnds.end ? null : (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => stepRow(-1)}
                      disabled={rowEnds.start}
                      aria-label={text('cart.drawer_previous')}
                      aria-controls={rowId}
                      className="p-1 transition-opacity hover:opacity-70 disabled:cursor-default disabled:opacity-30"
                    >
                      <ArrowLeft className="size-5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => stepRow(1)}
                      disabled={rowEnds.end}
                      aria-label={text('cart.drawer_next')}
                      aria-controls={rowId}
                      className="p-1 transition-opacity hover:opacity-70 disabled:cursor-default disabled:opacity-30"
                    >
                      <ArrowRight className="size-5" aria-hidden />
                    </button>
                  </div>
                )}
              </div>
              <div
                id={rowId}
                ref={rowRef}
                onScroll={measureRow}
                className="-mx-6 flex snap-x scroll-px-6 scroll-smooth gap-3 overflow-x-auto px-6 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {suggestions.map((product) => (
                  <Link
                    key={product.id}
                    to={paths.product(product.slug)}
                    className="w-40 shrink-0 snap-start"
                    style={{ backgroundColor: 'var(--storefront-surface)' }}
                  >
                    <span className="block aspect-square overflow-hidden">
                      {product.image_url ? (
                        <StorefrontImage
                          url={product.image_url}
                          sizes={fixedImageSizes(160)}
                          alt=""
                          className="size-full object-cover"
                          style={focalStyle(product.image_focal ?? null)}
                        />
                      ) : null}
                    </span>
                    <span className="block px-3 py-2.5">
                      <span className="line-clamp-2 block text-sm font-semibold">{product.name}</span>
                      <span className="mt-1 block text-sm">{formatMoney(product.price)}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {items.length > 0 && cart.data ? (
          <div className="border-t px-6 py-5" style={hairline}>
            {split.bundle > 0 ? (
              <div className="mb-1 flex justify-between text-sm" style={{ color: 'var(--storefront-muted)' }}>
                <span>{text('cart.bundle_discount')}</span>
                <span>-{formatMoney(split.bundle)}</span>
              </div>
            ) : null}
            {split.coupon > 0 ? (
              <div className="mb-1 flex justify-between text-sm" style={{ color: 'var(--storefront-muted)' }}>
                <span>{cart.data.discount_code ?? text('cart.discount')}</span>
                <span>-{formatMoney(split.coupon)}</span>
              </div>
            ) : null}
            <div className="flex justify-between text-lg font-bold">
              <span style={STOREFRONT_HEADING_STYLE}>{text('cart.subtotal')}</span>
              <span>{formatMoney(cart.data.subtotal)}</span>
            </div>
            <p className="mt-1 text-xs" style={{ color: 'var(--storefront-muted)' }}>
              {text('cart.drawer_note')}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Link
                to={paths.cart()}
                className="border px-4 py-3 text-center text-sm font-semibold uppercase tracking-wide hover:opacity-80"
                style={{ borderColor: 'var(--storefront-text)', borderRadius: 'var(--storefront-radius)' }}
              >
                {text('cart.view_cart')}
              </Link>
              {features.checkout ? (
                overStock ? (
                  <span
                    role="link"
                    aria-disabled="true"
                    className="cursor-not-allowed px-4 py-3 text-center text-sm font-semibold uppercase tracking-wide opacity-50"
                    style={{ ...STOREFRONT_BUTTON_STYLE, borderRadius: 'var(--storefront-radius)' }}
                  >
                    {text('cart.checkout')}
                  </span>
                ) : (
                  <Link
                    to={paths.checkout()}
                    className="px-4 py-3 text-center text-sm font-semibold uppercase tracking-wide hover:opacity-90"
                    style={{ ...STOREFRONT_BUTTON_STYLE, borderRadius: 'var(--storefront-radius)' }}
                  >
                    {text('cart.checkout')}
                  </Link>
                )
              ) : null}
            </div>
            {features.checkout && overStock ? (
              <p role="status" className="mt-3 text-xs text-red-500">
                {shopCopy(
                  text,
                  'cart.over_stock_checkout',
                  'Some items have fewer left than you have chosen. Reduce them to check out.',
                )}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
